import './Settings.styles.scss';

import { observer } from 'mobx-react';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { FileUpload } from 'primereact/fileupload';
import { InputNumber } from 'primereact/inputnumber';
import { InputSwitch } from 'primereact/inputswitch';
import React, { FC, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { Permission } from '../../model/enumeration/Permission.enum';
import { NotificationSettings } from '../../model/settings/NotificationSettings';
import { NotificationSectionKey } from '../../model/settings/UserSettings';
import audioStore from '../../store/AudioStore';
import authStore from '../../store/AuthStore';
import chatStore from '../../store/ChatStore';
import notificationStore from '../../store/NotificationStore';
import permissionStore from '../../store/PermissionStore';
import pushNotificationStore from '../../store/PushNotificationStore';
import translateStore from '../../store/TranslateStore';
import userSettingsStore from '../../store/UserSettingsStore';
import { deepCopy, getValueByKey } from '../../utils/common.utils';
import { Paths } from '../Router/Paths';
import { NotificationSection } from './Settings.types';

const Settings: FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [expertMode, setExpertMode] = useState(
    userSettingsStore.settings.table.expertMode,
  );

  const audioUploadRef = useRef<FileUpload>(null);

  const initSettings = useMemo(
    () => deepCopy(userSettingsStore.settings),
    [userSettingsStore.settings, isOpen],
  );

  const hasManualBotMoveControlPermission = useMemo(
    () => permissionStore.hasPermission(Permission.MANUAL_BOT_MOVE_CONTROL),
    [authStore.authenticatedUser],
  );

  const hasManageBotsSeatingPermission = useMemo(
    () => permissionStore.hasPermission(Permission.MANAGE_BOTS_SEATING),
    [authStore.authenticatedUser],
  );

  const hasManageBotsPermission = useMemo(
    () => permissionStore.hasPermission(Permission.MANAGE_BOTS),
    [authStore.authenticatedUser],
  );

  const notificationSections: NotificationSection[] = useMemo(
    () => [
      { prefix: 'Chat text', key: NotificationSectionKey.CHAT_TEXT },
      { prefix: 'Chat emoji', key: NotificationSectionKey.CHAT_EMOJI },
      {
        prefix: 'New live player',
        key: NotificationSectionKey.NEW_LIVE_PLAYER_NOTIFICATIONS,
      },
      {
        prefix: 'Live player won/lost 100 BB',
        key: NotificationSectionKey.LIVE_PLAYER_WON_N_BB_NOTIFICATIONS,
      },
      {
        prefix: 'Bot left table',
        key: NotificationSectionKey.BOT_LEFT_TABLE_NOTIFICATIONS,
      },
      {
        prefix: 'Live player STRADDLE',
        key: NotificationSectionKey.LIVE_PLAYER_BET_STRADDLE_NOTIFICATIONS,
      },
    ],
    [],
  );

  const location = useLocation();

  const onHide = (): void => {
    const settingsToUpdate = deepCopy(userSettingsStore.settings);
    settingsToUpdate.table.expertMode = expertMode;

    userSettingsStore
      .updateUserSettings(settingsToUpdate)
      .catch(response => {
        notificationStore.showNotification({
          severity: 'error',
          summary: response.response.data.error || 'Error',
          detail: `Error while updating user settings`,
        });
        userSettingsStore.setUserSettings(initSettings);
      })
      .finally(() => setIsOpen(false));
  };

  const onHideMessageDelayChange = (
    seconds: number,
    key: NotificationSectionKey,
  ): void => {
    userSettingsStore.modifyNotificationSettings(
      {
        hideNotificationsDelay: seconds * 1000,
      },
      key,
    );
  };

  const onAutoCloseTablesChange = (seconds: number): void => {
    userSettingsStore.modifyTableSettings({
      autoCloseTables: seconds * 1000,
    });
  };

  const onChatDisableChange = (disabled: boolean): void => {
    userSettingsStore.modifyChatSettings({ disabled });
    if (disabled) {
      chatStore.resetState();
    }
  };

  const onPushNotificationEnableChange = async (
    enabled: boolean,
  ): Promise<void> => {
    if (enabled) {
      const isGranted = await pushNotificationStore.enablePushNotifications();
      if (!isGranted) {
        notificationStore.showNotification({
          severity: 'error',
          summary: 'Error',
          detail: (
            <p>
              You have disabled notifications in the browser. To enable it, go
              to: <br /> <br /> Chrome → Settings → Privacy → Notifications →
              Allow
            </p>
          ),
        });
      }
      userSettingsStore.modifyPushNotificationSettings({ enabled: isGranted });
    } else {
      userSettingsStore.modifyPushNotificationSettings({ enabled });
      await pushNotificationStore.unsubscribePushNotifications();
    }
  };

  const onLanguageChange = (language: string): void => {
    userSettingsStore.modifyChatSettings({ language });
    translateStore.clearCache();
  };

  const languageOptions = useMemo(
    () =>
      translateStore.getAvailableLanguages().map(language => ({
        label: language,
        value: language.toUpperCase(),
      })),
    [translateStore.availableLanguages],
  );

  const onSelectAudioFile = (
    files: File[],
    key: NotificationSectionKey,
  ): void => {
    audioStore.upload(files, key);
    audioUploadRef.current?.clear();
  };

  const isChatSettingsDisabled = (key: NotificationSectionKey): boolean =>
    [
      NotificationSectionKey.CHAT_EMOJI,
      NotificationSectionKey.CHAT_TEXT,
    ].includes(key) && userSettingsStore.settings.chat.disabled;

  return (
    <>
      <Button
        className="settings-btn me-1"
        icon="pi pi-cog"
        rounded
        text
        severity="secondary"
        aria-label="Setting"
        tooltip="Settings"
        onClick={() => setIsOpen(true)}
      />
      <Dialog
        className="Settings-Component"
        contentClassName="pt-3"
        visible={isOpen}
        onHide={onHide}
        header="Settings"
        dismissableMask
        maskClassName="ignore-outside-click"
        data-ignore-outside-click
      >
        <Accordion multiple className="section" activeIndex={[0, 1]}>
          <AccordionTab header="Tables">
            <div className="settings-input-switch">
              <div className="d-flex align-items-center justify-content-between extra-margin">
                <div>
                  <label htmlFor="lims-x100">
                    <i className="pi pi-dollar icon" />
                    Lims x100
                  </label>
                </div>
                <InputSwitch
                  inputId="lims-x100"
                  checked={userSettingsStore.settings.table.limsX100}
                  onChange={e =>
                    userSettingsStore.modifyTableSettings({
                      limsX100: e.value,
                    })
                  }
                />
              </div>
              {hasManualBotMoveControlPermission && (
                <div className="d-flex align-items-center justify-content-between extra-margin">
                  <div>
                    <label htmlFor="manualBotMoveControl">
                      <i className="pi pi-user icon" />
                      Manual bot move control
                    </label>
                  </div>
                  <InputSwitch
                    inputId="manualBotMoveControl"
                    checked={
                      userSettingsStore.settings.table.manualBotMoveControl
                    }
                    onChange={e =>
                      userSettingsStore.modifyTableSettings({
                        manualBotMoveControl: e.value,
                      })
                    }
                  />
                </div>
              )}
              <div className="d-flex align-items-center justify-content-between extra-margin">
                <div>
                  <label htmlFor="country-flag">
                    <i className="pi pi-globe icon" />
                    Country flag
                  </label>
                </div>
                <InputSwitch
                  inputId="country-flag"
                  checked={userSettingsStore.settings.table.countryFlag}
                  onChange={e =>
                    userSettingsStore.modifyTableSettings({
                      countryFlag: e.value,
                    })
                  }
                />
              </div>
              <div className="d-flex align-items-center justify-content-between extra-margin">
                <div>
                  <label htmlFor="vpip">
                    <i className="pi pi-wave-pulse icon" />
                    VPIP
                  </label>
                </div>
                <InputSwitch
                  inputId="vpip"
                  checked={userSettingsStore.settings.table.vpip}
                  onChange={e =>
                    userSettingsStore.modifyTableSettings({
                      vpip: e.value,
                    })
                  }
                />
              </div>
              {hasManageBotsSeatingPermission && (
                <div className="d-flex align-items-center justify-content-between extra-margin">
                  <div>
                    <label htmlFor="ask-buyin-before-sit">
                      <i className="pi pi-money-bill icon" />
                      Ask buyIn before sit
                    </label>
                  </div>
                  <InputSwitch
                    inputId="ask-buyin-before-sit"
                    checked={userSettingsStore.settings.table.askBuyInBeforeSit}
                    onChange={e =>
                      userSettingsStore.modifyTableSettings({
                        askBuyInBeforeSit: e.value,
                      })
                    }
                  />
                </div>
              )}
              <div className="d-flex align-items-center justify-content-between extra-margin">
                <div>
                  <label htmlFor="light-mode">
                    <i className="pi pi-cloud icon" />
                    Light mode
                  </label>
                </div>
                <InputSwitch
                  inputId="light-mode"
                  checked={userSettingsStore.settings.table.lightMode}
                  onChange={e =>
                    userSettingsStore.modifyTableSettings({
                      lightMode: e.value,
                    })
                  }
                />
              </div>
              <div className="d-flex align-items-center justify-content-between extra-margin">
                <div>
                  <label htmlFor="expert-mode">
                    <i className="pi pi-wrench icon" />
                    Expert mode
                  </label>
                </div>
                <InputSwitch
                  inputId="expert-mode"
                  checked={expertMode}
                  onChange={e => setExpertMode(e.value)}
                />
              </div>
              {hasManageBotsPermission && (
                <div className="d-flex align-items-center justify-content-between extra-margin">
                  <div>
                    <label htmlFor="rebuy">
                      <i className="pi pi-wallet icon" />
                      Rebuy
                    </label>
                  </div>
                  <InputSwitch
                    inputId="rebuy"
                    checked={userSettingsStore.settings.table.rebuy}
                    onChange={e =>
                      userSettingsStore.modifyTableSettings({
                        rebuy: e.value,
                      })
                    }
                  />
                </div>
              )}
            </div>
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <label htmlFor="auto-close-tables">
                  <i className="pi pi-times-circle icon" />
                  Auto-close deleted tables delay (seconds)
                </label>
              </div>
              <InputNumber
                className="input-number"
                inputId="auto-close-tables"
                min={0}
                onChangeCapture={(e: any) =>
                  onAutoCloseTablesChange(e.target?.value ?? 0)
                }
                onChange={e => onAutoCloseTablesChange(e.value)}
                value={userSettingsStore.settings.table.autoCloseTables / 1000}
              />
            </div>
          </AccordionTab>
          <AccordionTab header="Chat">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <div>
                <label htmlFor="chat-disabling">
                  <i className="pi pi-eye-slash icon" />
                  Disable chat
                </label>
              </div>
              <InputSwitch
                inputId="chat-disabling"
                checked={userSettingsStore.settings.chat.disabled}
                onChange={e => onChatDisableChange(e.value)}
                disabled={location.pathname === Paths.HISTORY}
              />
            </div>
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <label htmlFor="chat-language">
                  <i className="pi pi-language icon" />
                  Language
                </label>
              </div>
              <Dropdown
                inputId="chat-language"
                className="w-50"
                value={userSettingsStore.settings.chat.language}
                onChange={e => onLanguageChange(e.value)}
                options={languageOptions}
                optionLabel="label"
                optionValue="value"
                placeholder="Select a language"
                filter
              />
            </div>
          </AccordionTab>
          <AccordionTab header="Notifications">
            {notificationSections.map(({ key, prefix }) => {
              const notificationSettings = getValueByKey<NotificationSettings>(
                userSettingsStore.settings,
                key,
              );
              return (
                notificationSettings && (
                  <div className="notification-section" key={key}>
                    <div className="notification-properties sound-setting align-items-center">
                      <div>
                        <label htmlFor={`${key}-sound`}>
                          <i className="pi pi-volume-up icon" />
                          {`${prefix} sound`}
                        </label>
                      </div>
                      <InputSwitch
                        inputId={`${key}-sound`}
                        checked={notificationSettings.sound}
                        disabled={
                          !notificationSettings.notifications ||
                          isChatSettingsDisabled(key)
                        }
                        onChange={e =>
                          userSettingsStore.modifyNotificationSettings(
                            { sound: e.value },
                            key,
                          )
                        }
                      />
                    </div>
                    <div className="notification-audio-properties align-items-center mb-2">
                      <FileUpload
                        className="notification-audio-uploader"
                        ref={audioUploadRef}
                        mode="basic"
                        name="audio"
                        accept="audio/*"
                        chooseLabel="Choose audio"
                        customUpload
                        auto={false}
                        onSelect={value => onSelectAudioFile(value.files, key)}
                        disabled={
                          !notificationSettings.sound ||
                          !notificationSettings.notifications ||
                          isChatSettingsDisabled(key)
                        }
                      />
                      <Button
                        className="notification-audio-button"
                        icon="pi pi-play"
                        onClick={() => audioStore.play(key)}
                        outlined
                        rounded
                        disabled={
                          !notificationSettings.sound ||
                          !notificationSettings.notifications ||
                          isChatSettingsDisabled(key)
                        }
                      />
                      <Button
                        className="notification-audio-button"
                        icon="pi pi-refresh"
                        onClick={() =>
                          audioStore.reset(notificationSettings.audio, key)
                        }
                        outlined
                        rounded
                        disabled={
                          !notificationSettings.sound ||
                          !notificationSettings.notifications ||
                          isChatSettingsDisabled(key)
                        }
                      />
                    </div>
                    <div className="notification-properties align-items-center mb-2">
                      <div>
                        <label htmlFor={`${key}-notifications`}>
                          <i className="pi pi-bell icon" />
                          {`${prefix} notifications`}
                        </label>
                      </div>
                      <InputSwitch
                        inputId={`${key}-notifications`}
                        checked={notificationSettings.notifications}
                        disabled={isChatSettingsDisabled(key)}
                        onChange={e =>
                          userSettingsStore.modifyNotificationSettings(
                            {
                              notifications: e.value,
                            },
                            key,
                          )
                        }
                      />
                    </div>
                    <div className="notification-properties align-items-center">
                      <div>
                        <label htmlFor={`${key}-hide-message-delay`}>
                          <i className="pi pi-times-circle icon" />
                          Hide delay (seconds)
                        </label>
                      </div>
                      <InputNumber
                        className="input-number"
                        inputId={`${key}-hide-message-delay`}
                        min={0}
                        disabled={
                          !notificationSettings.notifications ||
                          isChatSettingsDisabled(key)
                        }
                        onChangeCapture={(e: any) =>
                          onHideMessageDelayChange(e.target?.value ?? 0, key)
                        }
                        onChange={e => onHideMessageDelayChange(e.value, key)}
                        value={
                          notificationSettings.hideNotificationsDelay / 1000
                        }
                      />
                    </div>
                  </div>
                )
              );
            })}
          </AccordionTab>
          <AccordionTab header="Push notifications">
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <label htmlFor="push-notification-enabling">
                  <i className="pi pi-bell icon" />
                  Push notifications
                </label>
              </div>
              <InputSwitch
                inputId="push-notification-enabling"
                checked={userSettingsStore.settings.pushNotifications.enabled}
                onChange={e => onPushNotificationEnableChange(e.value)}
              />
            </div>
          </AccordionTab>
        </Accordion>
      </Dialog>
    </>
  );
};

export default observer(Settings);
